import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FrameworkTreeService } from 'app/shared/framework-tree.service';
import { v4 as uuid } from 'uuid';
import {
  CreateRiskInput,
  DeleteRiskInput,
  RiskPriorityEnum,
  RiskQuestionScoreInput,
  RoleEnum,
  RiskStatusEnum,
  CreateAnswerInput,
  RiskAction,
  GetEntityQuery,
  RiskConfidenceEnum,
  ModelRiskFilterInput,
  TaskStatusEnum,
  CreateCommentInput,
  GetCommentQuery,
  UpdateCommentInput,
  DeleteCommentInput,
  CommentsByRiskIdQuery,
  CreateLogsInput,
  LogsByTargetIdQuery,
  CreateSavedFilterInput,
  UpdateSavedFilterInput,
  CreateRiskLineChartMutation,
  RiskImpactEnum,
  ModelRiskLineChartFilterInput,
  GetEntityWizardQuery,
  CreateRiskGroupInput,
  LabelType,
  PartialRepresentationEnum,
} from 'app/API.service';
import { UtilsService } from 'app/shared/utils.service';
import { RiskTypeEnum, StandardType } from 'app/API.service';
import { Router, ActivatedRoute } from '@angular/router';
import { EntityService } from 'app/shared/entity.service';
import { IRiskTypes } from 'app/shared/types&Interfaces';
import { RISK_RESIDUAL_FLOOR, RiskActionType, RiskTableConstant } from './risk-register.constant';
import { ImpactEnum, ProbabilityEnum, RiskColumnMappersEnum, StatusEnum } from 'app/shared/enums/risk-map.enums';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { RootEntityContextService } from 'app/shared/root-entity-context.service';
import { getLogMessage } from 'app/shared/helpers/logs.helper';
import { LogsKeyEnum, LogsTypeEnum } from 'app/shared/enums/logsKey.enum';
import { RISK_RESIDUAL_SCORE_ENUM } from './risk-register.constant';

@Injectable()
export class RiskRegisterService {
  dsc: boolean = false;
  isArchiveView: boolean = false;
  controlsList: Record<string, any> = {};
  editModeEnabled: Subject<boolean> = new Subject<boolean>();
  createCommentData$ = new Subject<any>();
  riskDetailCloser: Subject<boolean> = new Subject<boolean>();
  isRiskDetailOpen: Subject<boolean> = new Subject<boolean>();
  riskDetailOpener: Subject<any> = new Subject<any>();
  showAllRisks: Subject<any> = new Subject<any>();
  saveCurrentRiskData: Subject<boolean> = new Subject<boolean>();
  updateAllRiskList: Subject<any> = new Subject<any>();
  updateTagsList: Subject<any> = new Subject<any>();
  updateCategoriesList: Subject<any> = new Subject<any>();
  updateCachedRiskProp: Subject<any> = new Subject<any>();
  triggerBulkPop: Subject<boolean> = new Subject<boolean>();
  dlmRestoreTrigger$ = new Subject<void>();
  riskGraphLineData: any[] = [];
  lastLineChartRiskId: string = '';
  nistRisks: any = null;
  nistMappedRC: any = null;
  allRisksMappedRC: any = {};
  rskQuesCount: any = {};
  riskMangersList: any = [];
  currentUser: any = null;
  defaultFilter: any = null;
  rootEntityId!: string;
  subEntities: GetEntityQuery[] = [];
  subEntitiesByRootId: GetEntityQuery[] = [];
  domain = '';
  cachedGroupsList: any[] = [];
  // categorizedTags = {};
  riskTableData: any = {
    groupData: [],
    allRisk: {},
  };
  controlsAgainstSubEntity: any;
  previousCircle: any;
  private previousCircleSubject = new BehaviorSubject<any>({});
  private riskCache = new Map<string, any>();
  previousCircleSubject$ = this.previousCircleSubject.asObservable();
  defaultFilters: any;
  private cachedDefaultFiltersForEntityId: string | null = null; // Track which entity's filters are cached
  allTagsAgainstRootEntity: any = {};
  allCategoriesAgainstRootEntity: any = {};
  // In-flight deduplication for controls loading: parallel callers for the same assessmentId share one fetch
  private _controlsLoadingInFlight: { [assessmentId: string]: Promise<void> } = {};
  // In-flight deduplication for risk creation: prevent duplicate mutations from rapid clicks
  public _createRiskInFlight: { [riskId: string]: Promise<any> } = {};

  constructor(
    private toastrService: ToastrService,
    private frameworkTreeService: FrameworkTreeService,
    private customApi: CUSTOMAPIService,
    private router: Router,
    private route: ActivatedRoute,
    private entityService: EntityService,
    private rootEntityContextService: RootEntityContextService
  ) {
    this.domain = window.location.origin;
  }

  triggerDlmRestore(): void {
    this.dlmRestoreTrigger$.next();
  }

  initializeRiskRegisterService(): void {
    // getting each risks total Questions count.
    this.rskQuesCount = {};

    if (this.nistMappedRC?.mappedRisks?.length) {
      this.nistMappedRC.mappedRisks.forEach((mr: any) => {
        const allConnectedRisks = mr.riskID ? mr.riskID.split(',') : [];
        allConnectedRisks.forEach((gr: any) => {
          const riskName = gr.trim();
          if (this.rskQuesCount[riskName] && this.rskQuesCount[riskName] > 0) {
            this.rskQuesCount[riskName]++;
          } else {
            this.rskQuesCount[riskName] = 1;
          }
        });
      });
    }
  }

  addPreviousCircle(circle: any) {
    this.previousCircle = circle;
    this.previousCircleSubject.next(circle);
  }

  getPreviousCircle() {
    return this.previousCircle;
  }

  set initializeRootEntityId(id: string) {
    this.rootEntityId = id;
  }

  getRiskTotalQuestionCount(riskName: string): number | null {
    if (this.rskQuesCount && this.rskQuesCount[riskName]) {
      return this.rskQuesCount[riskName];
    } else {
      return null;
    }
  }

  isRiskFrameworkType(frameworkName: string): boolean {
    const frameworkWithRisksFiles: any[] = [
      'NIST_CSF',
      'NIST_CSF_BB',
      'NIST_OU',
      'ITC_CUSTOM',
      'PARAGON_FULL',
      'PARAGON_BASIC',
      'BEAZLEY',
      'CSF_SC',
      'NIST_800_171_RISK',
      'BEAZLEY_OT',
      'B_APP_RSWOT',
      'MITRE_ATTACK',
      'CYBERARK_CSA',
      'CYBERARK_NIST_CSF_CRITICAL',
    ];
    return frameworkWithRisksFiles.includes(frameworkName);
  }

  async getStaticRisks(frameworkName: string): Promise<any> {
    if (this.nistRisks) {
      return this.nistRisks;
    } else if (this.isRiskFrameworkType(frameworkName)) {
      this.nistRisks = await this.frameworkTreeService.getRiskControlsFromS3('risks-list', frameworkName);
      return this.nistRisks;
    }
  }

  async getMappedRisks(frameworkName: string): Promise<any> {
    if (this.nistMappedRC) {
      return this.nistMappedRC;
    } else if (this.isRiskFrameworkType(frameworkName)) {
      this.nistMappedRC = await this.frameworkTreeService.getRiskControlsFromS3('mapped-risks', frameworkName);
      this.initializeRiskRegisterService();
      return this.nistMappedRC;
    }
  }

  async getRiskLists(listName: string, frameworkName: string): Promise<any> {
    const riskList = await this.frameworkTreeService.getRiskControlsFromS3(listName, frameworkName);
    return riskList;
  }

  async getOptimizedRisks(subEntities: any[]): Promise<{ [key: string]: any[] }> {
    const riskMap: { [key: string]: any[] } = {};

    if (subEntities.length <= 7) {
      const promises: Promise<void>[] = [];

      for (const ent of subEntities) {
        const id = ent.activeAssessmentId;

        if (this.riskCache.has(id)) {
          riskMap[id] = this.riskCache.get(id);
        } else {
          const promise = this.getRisksList(id, true, true).then((risks: any) => {
            const enrichedRisks = risks.map((risk: any) => ({
              ...risk,
              subEntityName: ent.name,
              weightage: ent.weightageScore,
              isLocked: ent.isLocked,
            }));

            this.riskCache.set(id, enrichedRisks);
            riskMap[id] = enrichedRisks;
          });

          promises.push(promise);
        }
      }

      await Promise.all(promises);
    }

    // If more than 7, don't pre-fetch, just return empty map
    return riskMap;
  }

  async getRisksForSubEntity(id: string): Promise<any[]> {
    if (this.riskCache.has(id)) {
      return this.riskCache.get(id);
    }

    const risks = await this.getRisksList(id, true, true);
    this.riskCache.set(id, risks);
    return risks;
  }

  getLabelValue(value: any) {
    if (value <= 2.5) {
      return RiskImpactEnum.LOW;
    } else if (value <= 5) {
      return RiskImpactEnum.MEDIUM;
    } else if (value <= 7.5) {
      return RiskImpactEnum.HIGH;
    } else if (value <= 10) {
      return RiskImpactEnum.CRITICAL;
    }
    return '';
  }

  async getSingleWizardEntity(id: string): Promise<GetEntityWizardQuery | undefined> {
    try {
      return await this.customApi.GetEntityWizard(id);
    } catch (e: any) {
      console.log(e);
    }
  }

  async getTaskActionsByAssessmentIdAndTabName(assessmentId: string | null = null, tab: string) {
    const tabName = {
      eq: UtilsService.capitalizeFirstLetter(tab.toLowerCase()),
    };
    try {
      let results: any[] = [];
      let nextToken: string | null | undefined;
      do {
        const result = await this.customApi.TaskActionsByAssessmentIdAndTabName(
          assessmentId || '',
          tabName,
          undefined,
          undefined,
          1000,
          nextToken || undefined
        );
        results = results.concat(result.items);
        nextToken = result.nextToken;
      } while (nextToken);

      return results;
    } catch (error: any) {
      console.log(error);
    }
  }

  enablingEditMode(value: boolean): void {
    this.editModeEnabled.next(value);
  }

  isRiskDetailsOpen(value: boolean): void {
    this.isRiskDetailOpen.next(value);
  }

  saveRiskDataTrigger(): void {
    this.saveCurrentRiskData.next(true);
  }

  openRiskDetails(risk: any): void {
    this.riskDetailOpener.next(risk);
  }

  closeRiskDetails(value: boolean): void {
    this.riskDetailCloser.next(value);
  }

  isShowAllRisks(risk: any): void {
    this.showAllRisks.next(risk);
  }

  async createNewRisk(risk: CreateRiskInput, showToaster: boolean = false): Promise<any> {
    // In-flight deduplication: if a risk with this id is already being created,
    // wait for that request and return its result (prevent duplicate mutations from rapid clicks)
    if (this._createRiskInFlight[risk.id]) {
      return this._createRiskInFlight[risk.id];
    }

    const createPromise = (async () => {
      try {
        const dbRisk = await this.customApi.CreateRisk(risk);
        // Write-through to the RootEntityContextService risks cache (mirrors updateRisk) so consumers
        // see the new risk immediately instead of waiting for the AppSync create event.
        // this.rootEntityContextService.updateRiskInCache(dbRisk);
        if (showToaster) {
          // intentionally add this text. so client don't confuse with risk update or create.
          // because for normal case we show risk updated text, so for creating we also use same text.
          this.toastrService.success('Risk Created!');
          this.updateAllRiskList.next({ type: 'created', risk: dbRisk, editMode: false });
        }
        return dbRisk;
      } catch (error: any) {
        this.toastrService.error('Cannot create risk');
        console.log('Create New Risk Error -->', error);
        throw error;
      }
    })();

    this._createRiskInFlight[risk.id] = createPromise;
    try {
      return await createPromise;
    } finally {
      delete this._createRiskInFlight[risk.id];
    }
  }

  /**
   *
   * @param riskGroup
   * @param showToaster
   * @returns
   */
  async createNewRiskGroup(riskGroup: CreateRiskGroupInput, _showToaster: boolean = false): Promise<void> {
    void _showToaster;
    try {
      const dbRisk = await this.customApi.CreateRiskGroup(riskGroup);
      if (dbRisk) {
        this.toastrService.success('Group created successfully');
        this.updateAllRiskList.next({ type: 'created', risk: dbRisk, editMode: false });
      }
    } catch {
      this.toastrService.error('Cannot create risk group');
    }
  }

  async deductAICredit(entityId: string, aIData: any): Promise<void> {
    try {
      delete aIData?.__typename;
      aIData.usedCredits = aIData.usedCredits + 1;
      await this.customApi.updateAICredits({ id: entityId, AI: aIData, requestType: 'updateUsedCredits' });
    } catch (error: any) {
      console.log('Deduct AI Credit Error -->', error);
    }
  }

  async createDefaultFilter(filter: CreateSavedFilterInput) {
    try {
      const savedFilter = await this.customApi.CreateSavedFilter(filter);
      if (savedFilter) {
        this.defaultFilters.items.push(savedFilter);
      }
      return savedFilter;
    } catch (error: any) {
      console.log('Can not get Saved Filters -->', error);
    }
  }

  async getDefaultFilter(entityId: any, userId: any) {
    try {
      // Validate cache: if cached filters are for a different entity, treat as cache miss
      if (this.defaultFilters && this.cachedDefaultFiltersForEntityId === entityId) {
        return this.defaultFilters;
      }
      // Cache miss or entity changed — fetch fresh data
      this.defaultFilters = await this.customApi.SavedFiltersByEntityIdAndUserId(entityId, userId);
      this.cachedDefaultFiltersForEntityId = entityId; // Track which entity this cache belongs to
      return this.defaultFilters;
    } catch (error: any) {
      console.log('Can not get Saved Filters -->', error);
    }
  }

  async updateDefaultFilter(filter: UpdateSavedFilterInput) {
    try {
      const savedFilter = await this.customApi.UpdateSavedFilter(filter, { entityId: { eq: filter.entityId } });
      if (this.defaultFilters?.items?.length) {
        const index = this.defaultFilters.items.findIndex((f: any) => f.id === savedFilter.id);
        if (index > -1) {
          this.defaultFilters.items[index] = savedFilter;
        } else {
          this.defaultFilters.items.push(savedFilter);
        }
      } else {
        this.defaultFilters = { items: [savedFilter] };
      }
      return savedFilter;
    } catch (error: any) {
      console.log('Can not update Saved Filters -->', error);
    }
  }

  async saveFinancialImpact(riskId: any, assessmentId: any, content: any, showToast: any = true): Promise<void> {
    if (this.riskTableData.allRisk[assessmentId]) {
      const index = this.riskTableData.allRisk[assessmentId].findIndex((rsk: any) => rsk.id === riskId);
      if (index > -1) {
        this.riskTableData.allRisk[assessmentId][index].financialImpact = content.contentsData;
        this.riskTableData.allRisk[assessmentId][index].financialToggle = content.financialToggle;
        this.riskTableData.allRisk[assessmentId][index].primaryLoss = content.primaryLoss;
      }
      await this.updateRisk(this.riskTableData.allRisk[assessmentId][index], showToast, true);
    }
  }

  async updateRiskDeadline(
    riskId: string,
    deadline: number,
    assessmentId: string,
    isRiskGroup: boolean = false,
    entityId: any = null
  ): Promise<void> {
    try {
      if (isRiskGroup) {
        await this.customApi.UpdateRiskGroup(
          { id: riskId, deadline, updatedAt: new Date().toISOString() },
          { entityId: { eq: entityId } }
        );
        this.rootEntityContextService.updateRiskGroupInCache({
          __typename: 'RiskGroup',
          id: riskId,
          entityId,
          deadline,
        });
      } else {
        await this.customApi.UpdateRisk(
          { id: riskId, deadline, updatedAt: new Date().toISOString() },
          {
            assessmentId: { eq: assessmentId },
          }
        );
        this.rootEntityContextService.updateRiskInCache({ id: riskId, assessmentId, deadline });
      }
    } catch (e: any) {
      console.log('err:  ', e);
    }
  }

  async getGroupsList(entityId: string): Promise<any> {
    try {
      // Delegate to the shared cache (identical GroupsByRootId query, cached once per root).
      return await this.rootEntityContextService.getGroupsList(entityId);
    } catch (e: any) {
      console.log('Cannot get the groups ', e);
      return [];
    }
  }
  async calculateGroupData(currentRisk: any) {
    if (!currentRisk) {
      return;
    }

    if (!this.riskTableData?.groupData?.length) {
      const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
      await this.getRiskGroup(entityId);
    }

    for (const group of this.riskTableData.groupData) {
      if (!group.riskHistory) {
        continue;
      }

      let groupUpdated = false;

      for (const history of group.riskHistory) {
        if (history.riskId === currentRisk.id) {
          history.riskImpact = currentRisk.riskDetails[0]?.children[3]?.contentsData.toUpperCase();
          history.riskProbability = currentRisk.riskDetails[0]?.children[4]?.contentsData.toUpperCase();
          history.residualImpact = currentRisk.riskDetails[0]?.children[5]?.contentsData.toUpperCase();
          history.residualProbability = currentRisk.riskDetails[0]?.children[6]?.contentsData.toUpperCase();
          history.residualFinancial = currentRisk.riskDetails[1]?.children[2]?.contentsData;
          history.inherentFinancial = currentRisk.riskDetails[1]?.children[1]?.contentsData;
          history.residual = currentRisk.residualRisk;
          history.residualImpactValue = currentRisk.residualImpactValue;
          history.residualProbabilityValue = currentRisk.residualProbabilityValue;
          history.riskName = currentRisk.riskTitle;
          groupUpdated = true;
        }
      }

      if (groupUpdated) {
        this.riskGroupCalculation(group, true);
        await this.updateRisk(group, false, true, true);
      }
    }
  }

  riskGroupCalculation(group: any, isNewGroup: boolean): any {
    // 1- Sync the weightage
    let sumInherentImpact = 0;
    let sumInherentProbability = 0;
    let sumResidualImpact = 0;
    let sumResidualProbability = 0;
    let inherentFinancial = 0;
    let residualFinancial = 0;
    let effectiveness = 0;
    let inherent = 0;
    let residual = 0;
    const history = isNewGroup ? group.riskHistory : group.scoreHistory;

    // 2- Calculate impact and probability with weights and financials sum.
    if (isNewGroup) {
      const totalWeightage = 100;
      const lockedWeightage = history.filter((r: any) => r.isLocked).reduce((sum: any, r: any) => sum + r, 0);
      const unlockedCount = history.filter((r: any) => !r.isLocked).length;
      const remainingWeightage = totalWeightage - lockedWeightage;

      const baseWeight = Math.floor(remainingWeightage / unlockedCount);
      let remainder = remainingWeightage - baseWeight * unlockedCount;

      history.forEach((r: any) => {
        if (!r.isLocked) {
          r.weightage = baseWeight + (remainder > 0 ? 1 : 0);
          if (remainder > 0) {
            remainder--;
          }
        }
      });
    }

    history.forEach((risk: any) => {
      const riskWeightage = (risk?.weightage || 0) / 100;

      //* Inherent Risk Calculations
      sumInherentImpact += UtilsService.getLabelValueNum(risk.riskImpact) * riskWeightage;
      sumInherentProbability += UtilsService.getLabelValueNum(risk.riskProbability) * riskWeightage;

      //* Residual Risk Calculations
      residual += (risk?.residual || 0) * riskWeightage;

      let impactValue = 0;
      if (UtilsService.isDefined(risk.residualImpactValue)) {
        impactValue = risk.residualImpactValue;
      } else if (UtilsService.isDefined(risk.residualImpact)) {
        impactValue = UtilsService.getLabelValueNum(risk.residualImpact);
      }
      sumResidualImpact += impactValue * riskWeightage;

      let probabilityValue = 0;
      if (UtilsService.isDefined(risk.residualProbabilityValue)) {
        probabilityValue = risk.residualProbabilityValue;
      } else if (UtilsService.isDefined(risk.residualProbability)) {
        probabilityValue = UtilsService.getLabelValueNum(risk.residualProbability);
      }
      sumResidualProbability += probabilityValue * riskWeightage;

      //* Financial Calculations
      inherentFinancial += risk?.inherentFinancial || 0;
      residualFinancial += risk?.residualFinancial || 0;
    });

    // 3- calculate inherent residual and effectiveness.
    inherent = (sumInherentImpact + sumInherentProbability) / 2;
    effectiveness = ((inherent - residual) / inherent) * 10;

    // 4- fixing the boundary values.
    inherent = UtilsService.fixBoundaries(inherent);
    residual = UtilsService.fixBoundaries(residual);
    effectiveness = UtilsService.fixBoundaries(effectiveness);

    // Assigning values to group Object.
    group.residualImpact = this.getLabelValue(UtilsService.fixBoundaries(sumResidualImpact));
    group.residualProbability = this.getLabelValue(UtilsService.fixBoundaries(sumResidualProbability));
    group.riskImpact = this.getLabelValue(UtilsService.fixBoundaries(sumInherentImpact));
    group.riskProbability = this.getLabelValue(UtilsService.fixBoundaries(sumInherentProbability));
    group.inherentRisk = parseFloat(inherent.toFixed(2));
    group.residualRisk = parseFloat(residual.toFixed(2));
    group.inherentFinancial = inherentFinancial;
    group.residualFinancial = residualFinancial;
    group.effectiveness = parseFloat(effectiveness.toFixed(1));
    return group;
  }
  recalculateAllWeightages(
    riskList: any[],
    riskData: any
  ): {
    updatedRiskList: any[];
    updatedRiskData: any;
  } {
    const existingScoreHistory: any[] = riskData?.scoreHistory || [];

    // Map with riskId as the key
    const scoreHistoryMap = new Map<string, any>(existingScoreHistory.map((item: any) => [item.riskId, item]));

    const updatedRiskList = riskList.map((risk: any) => {
      const history: any = scoreHistoryMap.get(risk.riskId);

      return {
        ...risk,
        isLocked: history?.isLocked ?? false,
        weightage: history?.weightage ?? 0,
      };
    });

    const lockedRisks = updatedRiskList.filter((r: any) => r.isLocked);
    const unlockedRisks = updatedRiskList.filter((r: any) => !r.isLocked);

    const lockedWeight = lockedRisks.reduce((sum: number, r: any) => sum + r.weightage, 0);
    const remainingWeight = 100 - lockedWeight;

    const baseWeight = unlockedRisks.length > 0 ? Math.floor(remainingWeight / unlockedRisks.length) : 0;
    let remainder = unlockedRisks.length > 0 ? remainingWeight % unlockedRisks.length : 0;

    const finalRiskList = updatedRiskList.map((risk: any) => {
      if (!risk.isLocked) {
        risk.weightage = baseWeight + (remainder > 0 ? 1 : 0);
        if (remainder > 0) {
          remainder--;
        }
      }
      return risk;
    });

    const updatedRiskData = {
      ...riskData,
      scoreHistory: finalRiskList,
      riskIds: finalRiskList.map((r: any) => r.riskId),
    };

    return {
      updatedRiskList: finalRiskList,
      updatedRiskData,
    };
  }

  deleteAndRecalculateWeightages(
    filteredGroupRiskList: any[],
    riskData: any,
    selectedRisks: any[]
  ): {
    updatedRiskList: any[];
    updatedRiskData: any;
  } {
    const updatedRiskList: any[] = [];

    const updatedScoreHistory = riskData.scoreHistory.filter(
      (score: any) =>
        !selectedRisks.some((sel: any) => sel.riskIdTitle === score.riskIdTitle && sel.entityName === score.entityName)
    );
    const lockedRisks: any[] = [];
    const unlockedRisks: any[] = [];
    let totalWeightUsed = 0;

    filteredGroupRiskList.forEach((risk: any) => {
      if (
        selectedRisks.some((sel: any) => sel.riskIdTitle === risk.riskIdTitle && sel.entityName === risk.entityName)
      ) {
        return;
      }
      updatedRiskList.push(risk);

      if (risk.isLocked) {
        lockedRisks.push(risk);
        totalWeightUsed = totalWeightUsed + risk.weightage;
      } else {
        unlockedRisks.push(risk);
      }
    });

    const remainingWeight = 100 - totalWeightUsed;
    const baseWeight = Math.floor(remainingWeight / unlockedRisks.length);
    let remainder = remainingWeight % unlockedRisks.length;

    updatedRiskList.forEach((risk: any) => {
      const matchingScore = updatedScoreHistory.find(
        (score: any) => score.riskIdTitle === risk.riskIdTitle && score.entityName === risk.entityName
      );
      if (matchingScore && !risk.isLocked) {
        const assignedWeight = baseWeight + (remainder > 0 ? 1 : 0);
        matchingScore.weightage = assignedWeight;
        risk.weightage = assignedWeight;
        if (remainder > 0) {
          remainder--;
        }
      }
    });

    const selectedRiskIds = selectedRisks.map((risk: any) => risk.riskId);
    const updatedRiskIds = riskData.risksIds.filter((id: string) => !selectedRiskIds.includes(id));

    const updatedRiskData = {
      ...riskData,
      scoreHistory: updatedScoreHistory,
      riskIds: updatedRiskIds,
    };

    return {
      updatedRiskList,
      updatedRiskData,
    };
  }

  async updateRisk(
    risk: any,
    showToast: any = true,
    triggerUpdate: any = true,
    isRiskGroup: any = false
  ): Promise<void> {
    // removing __typename property from Score History of risk
    if (risk.scoreHistory && risk.scoreHistory.length) {
      risk.scoreHistory.forEach((scoreObj: any) => delete scoreObj.__typename);
    }

    // removing __typename property from risk History of risk
    if (risk.riskHistory && risk.riskHistory.length) {
      risk.riskHistory.forEach((riskHisObj: any) => delete riskHisObj.__typename);
    }

    // removing __typename property from financialImpact of risk
    if (risk && risk.financialImpact && risk.financialImpact.length) {
      risk.financialImpact.forEach((fi: any) => {
        if (fi.__typename) {
          delete fi.__typename;
        }
      });
    }

    // removing __typename property from Manual Risk Control Names
    if (risk && risk.riskControlNames && risk.riskControlNames.length) {
      risk.riskControlNames.forEach((name: any) => delete name.__typename);
    }
    // removing __typename property from risk task list
    const onlyManualTasks: any[] = [];
    if (risk && risk.riskTasks && risk.riskTasks.length) {
      risk.riskTasks.forEach((tsk: any) => {
        delete tsk.__typename;
        if (!tsk.autoTasks) {
          delete tsk.autoTasks;
          onlyManualTasks.push(tsk);
        }
        delete tsk.autoTasks;
      });
    }

    const localRisk = this.createInputObject(risk, isRiskGroup, onlyManualTasks);
    const inherent = (
      (UtilsService.getLabelValueNum(localRisk.riskImpact) + UtilsService.getLabelValueNum(localRisk.riskProbability)) /
      2
    ).toFixed(1);
    localRisk.inherentRisk = parseFloat(inherent);
    let result = null;
    try {
      if (isRiskGroup) {
        result = await this.customApi.UpdateRiskGroup(localRisk, { entityId: { eq: localRisk?.entityId } });
      } else {
        result = await this.customApi.UpdateRisk(localRisk, {
          assessmentId: { eq: localRisk?.assessmentId },
        });
        // keep the RootEntityContextService cache in sync with the edited risk
        this.rootEntityContextService.updateRiskInCache(result);
      }
      if (showToast) {
        this.toastrService.success(isRiskGroup ? 'Risk Group Updated!' : 'Risk Updated!');
      }
      if (triggerUpdate) {
        this.updateAllRiskList.next({ type: 'updated', risk: result });
      }
    } catch (e: any) {
      console.log('Error ---> ', e);
      if (e?.status === 400) {
        this.toastrService.error('Risk not found!!!');
      } else {
        this.toastrService.error('Could not update the risk', e?.message || e?.errors[0]?.message || e);
      }
    }
  }

  updateRiskCache(risk: any, isRiskGroup: boolean = false, type: string = ''): void {
    switch (type) {
      case RiskActionType.RISK_UPDATE:
      case RiskActionType.RISK_ARCHIVE: {
        // It will handle both archive and unarchive action.
        if (this.riskTableData.allRisk[risk.assessmentId] && !isRiskGroup) {
          const index = this.riskTableData.allRisk[risk.assessmentId].findIndex((r: any) => r.id === risk.id);
          if (index > -1) {
            this.riskTableData.allRisk[risk.assessmentId][index] = risk;
          }
        } else if (this.riskTableData.groupData && isRiskGroup) {
          const index = this.riskTableData.groupData.findIndex((r: any) => r.id === risk.id);
          if (index > -1) {
            const formattedRisk = this.convertRiskToGroupFormat(risk);
            this.riskTableData.groupData[index] = formattedRisk;
          }
        }
        break;
      }
      case RiskActionType.RISK_DELETE: {
        if (this.riskTableData.allRisk[risk.assessmentId] && !isRiskGroup) {
          this.riskTableData.allRisk[risk.assessmentId] = this.riskTableData.allRisk[risk.assessmentId].filter(
            (r: any) => r.id !== risk.id
          );
        } else if (this.riskTableData.groupData && isRiskGroup) {
          this.riskTableData.groupData = this.riskTableData.groupData.filter((r: any) => r.id !== risk.id);
        }
        break;
      }
      case RiskActionType.RISK_CREATE: {
        if (!isRiskGroup) {
          const existingRisks = this.riskTableData.allRisk[risk.assessmentId] || [];
          if (!existingRisks.some((r: any) => r.id === risk.id)) {
            existingRisks.push(risk);
            this.riskTableData.allRisk[risk.assessmentId] = existingRisks;
          }
        } else {
          const formattedRisk = this.convertRiskToGroupFormat(risk);
          if (!this.riskTableData.groupData.some((r: any) => r.id === formattedRisk.id)) {
            this.riskTableData.groupData.push(formattedRisk);
          }
        }
        break;
      }
    }
  }

  convertRiskToGroupFormat(risk: any): any {
    return {
      createdAt: risk.createdAt,
      deadline: risk.deadline,
      effectiveness: risk.effectiveness,
      entityId: risk.entityId,
      groupDescription: risk.groupDescription || '',
      groupStatus: risk.groupStatus || 'OPEN',
      groupStatusUpdatedBy: risk.groupStatusUpdatedBy || 'PLATFORM',
      groupTitle: risk.groupTitle,
      id: risk.id,
      idTitle: risk.idTitle,
      inherentFinancial: risk.inherentFinancial || 0,
      inherentRisk: risk.inherentRisk || 0,
      isArchived: risk.isArchived || false,
      residualFinancial: risk.residualFinancial || 0,
      residualImpact: risk.residualImpact || 'LOW',
      residualProbability: risk.residualProbability || 'LOW',
      residualRisk: risk.residualRisk || 0,
      riskGroupId: risk.riskGroupId || '',
      riskHistory: risk.riskHistory || [],
      riskImpact: risk.riskImpact || 'LOW',
      riskMappingIds: risk.smartMappingIds || [],
      riskProbability: risk.riskProbability || 'LOW',
      risksIds: risk.risksIds || [],
      type: risk.type || 'AUTOMATIC',
      updatedAt: risk.updatedAt || new Date().toISOString(),
      __typename: 'RiskGroup',
    };
  }

  createInputObject(risk: any, isRiskGroup: boolean, onlyManualTasks: any): any {
    if (isRiskGroup) {
      return {
        id: risk.id,
        idTitle: risk.idTitle,
        entityId: risk.entityId,
        riskGroupId: risk.riskGroupId,
        type: risk.type,
        riskMappingIds: risk.smartMappingIds,
        risksIds: risk.risksIds,
        riskHistory: risk.scoreHistory || risk.riskHistory || [],
        groupTitle: risk.riskTitle,
        groupDescription: risk.riskDescription,
        residualImpact: risk.residualImpact,
        residualProbability: risk.residualProbability,
        riskImpact: risk.riskImpact,
        riskProbability: risk.riskProbability,
        inherentRisk: risk.inherentRisk,
        residualRisk: risk.residualRisk,
        inherentFinancial: risk.inherentFinancial,
        residualFinancial: risk.residualFinancial,
        groupStatus: risk.riskStatus,
        groupStatusUpdatedBy: risk.riskStatusUpdatedBy,
        createdAt: risk.createdAt,
        updatedAt: risk.updatedAt,
        isArchived: risk.isArchived,
        effectiveness: risk.effectiveness,
        deadline: risk.deadline,
      };
    } else {
      return {
        id: risk.id,
        idTitle: risk.idTitle,
        riskMappingId: risk.riskMappingId,
        riskTitle: risk.riskTitle,
        riskDescription: risk.riskDescription,
        relatedAssets: risk.relatedAssets,
        assessmentId: risk.assessmentId,
        riskImpact: risk.riskImpact.toUpperCase(),
        isEscalated: risk.isEscalated,
        riskProbability: risk.riskProbability.toUpperCase(),
        residualImpact: risk.residualImpact,
        residualProbability: risk.residualProbability,
        residualImpactValue: risk.residualImpactValue,
        residualProbabilityValue: risk.residualProbabilityValue,
        inherentRisk: risk.inherentRisk,
        residualRisk: risk.residualRisk,
        riskStatus: risk.riskStatus,
        riskStatusUpdatedBy: risk.riskStatusUpdatedBy,
        notes: risk.notes,
        costToRemediate: risk.costToRemediate,
        costToRemediateType: risk.costToRemediateType,
        primaryLoss: risk.primaryLoss,
        financialImpact: risk.financialImpact,
        riskPriority: risk.riskPriority,
        disable: risk.disable,
        hidden: risk.hidden ? risk.hidden : false,
        financialToggle: risk.financialToggle,
        controlIds: risk.controlIds && risk.controlIds.length ? risk.controlIds : [],
        smartMappingIds: risk.smartMappingIds && risk.smartMappingIds.length ? risk.smartMappingIds : [],
        scoreHistory: risk.scoreHistory && risk.scoreHistory.length ? risk.scoreHistory : [],
        riskTags: risk.riskTags && risk.riskTags.length ? risk.riskTags : null,
        riskTasks: onlyManualTasks && onlyManualTasks.length ? onlyManualTasks : null,
        riskControlNames: risk.riskControlNames && risk.riskControlNames.length ? risk.riskControlNames : null,
        riskTasksIds: risk.riskTasksIds && risk.riskTasksIds.length ? risk.riskTasksIds : null,
        sensitivity: risk.sensitivity,
        effectiveness: risk.effectiveness,
        riskOwnerIds: risk.riskOwnerIds,
        updatedAt: new Date().toISOString(),
      };
    }
  }

  async deleteRisk(risk: any): Promise<void> {
    try {
      const deleteObj: DeleteRiskInput = {
        id: risk.id,
      };
      await this.customApi.DeleteRisk(deleteObj, { assessmentId: { eq: risk?.assessmentId } });

      // Write-through to the RootEntityContextService risks cache (mirrors updateRisk) so the
      // deleted risk disappears immediately instead of waiting for the AppSync delete event.
      this.rootEntityContextService.removeRiskFromCache(risk);

      // Cascade-delete all dynamic labels (tags & categories) attached to this risk
      await this.deleteAllLabelsOfRisk(risk.id);

      // Cascade-update any groups that referenced this risk so their cache stays accurate
      await this.removeRiskFromGroups(risk.id);

      if (this.riskTableData.allRisk[risk.assessmentId]) {
        this.riskTableData.allRisk[risk.assessmentId] = this.riskTableData.allRisk[risk.assessmentId].filter(
          (cr: any) => cr.id !== risk.id
        );
      }
    } catch (e: any) {
      console.log(e);
      this.toastrService.error('Could not delete risk');
    }
  }

  /**
   * When a risk is deleted, strip it out of every group that referenced it: drop its riskHistory /
   * risksIds entry, recalculate the group and persist (which keeps the RootEntityContextService group
   * cache in sync via the 'updated' emit). Groups left with no risks are deleted.
   */
  private async removeRiskFromGroups(riskId: string): Promise<void> {
    try {
      if (!this.riskTableData?.groupData?.length) {
        const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
        await this.getRiskGroup(entityId);
      }
      for (const group of this.riskTableData.groupData || []) {
        if (!group?.riskHistory?.some((h: any) => h.riskId === riskId)) {
          continue;
        }
        group.riskHistory = group.riskHistory.filter((h: any) => h.riskId !== riskId);
        group.risksIds = (group.risksIds || []).filter((id: string) => id !== riskId);
        group.riskMappingIds = group.riskHistory.map((h: any) => h.riskMappingId).filter(Boolean);

        if (group.riskHistory.length) {
          this.riskGroupCalculation(group, true);
          await this.updateRisk(group, false, true, true);
        } else {
          await this.customApi.DeleteRiskGroup({ id: group.id }, { entityId: { eq: group.entityId } });
          this.rootEntityContextService.removeRiskGroupFromCache(group.id, group.entityId);
          this.updateAllRiskList.next({ type: 'deleted', risk: group, editMode: false });
        }
      }
    } catch (e) {
      console.log('Error removing deleted risk from groups', e);
    }
  }

  /**
   * Deletes all Tag and Category records whose targetId == riskId
   * (i.e. all dynamic labels attached to the given risk).
   */
  async deleteAllLabelsOfRisk(riskId: string): Promise<void> {
    try {
      if (!riskId) {
        return;
      }
      const entityId = this.rootEntityId || UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
      if (!entityId) {
        return;
      }

      const [tags, categories] = await Promise.all([
        this.getTagsByTargetId(riskId),
        this.getCategoriesByTargetId(riskId),
      ]);

      const promises: Promise<any>[] = [];
      (tags || []).forEach((t: any) => promises.push(this.deleteTagFromRisk(t.id)));
      (categories || []).forEach((c: any) => promises.push(this.deleteCategoryFromRisk(c.id)));

      if (promises.length) {
        await Promise.all(promises);
      }
    } catch (e: any) {
      console.log('Error - deleteAllLabelsOfRisk: ', e);
    }
  }

  async updateArchiveRiskProperty(risk: any, isArchived: any = false): Promise<void> {
    try {
      const result: any = await this.customApi.UpdateRisk(
        {
          id: risk.id,
          isArchived,
          updatedAt: new Date().toISOString(),
        },
        {
          assessmentId: { eq: risk?.assessmentId },
        }
      );
      if (result.errors && result.errors.length > 0) {
        this.toastrService.error(`Could not ${isArchived ? 'archive' : 're-activate'} risk`);
        return;
      }
      this.updateAllRiskList.next({ type: 'archived', risk: result });
    } catch (e: any) {
      console.log(e);
      this.toastrService.error(`Could not ${isArchived ? 'archive' : 're-activate'} risk`);
    }
  }

  async updateArchiveRiskGroupProperty(risk: any, entityId: any, isArchived: any = false): Promise<void> {
    try {
      const result = await this.customApi.UpdateRiskGroup(
        {
          id: risk.id,
          isArchived,
          updatedAt: new Date().toISOString(),
        },
        {
          entityId: { eq: entityId },
        }
      );
      this.updateAllRiskList.next({ type: 'archived', risk: result });
    } catch (e: any) {
      console.log(e);
      this.toastrService.error(`Could not ${isArchived ? 'archive' : 're-activate'} group}`);
    }
  }

  updateRiskTagsList(tags: any) {
    this.updateTagsList.next(tags);
  }

  updateRiskCategoriesList(categories: any) {
    this.updateCategoriesList.next(categories);
  }

  async escalateRisk(risk: any): Promise<boolean> {
    try {
      await this.customApi.UpdateRisk(
        {
          id: risk.id,
          isEscalated: true,
          riskImpact: risk.riskImpact.toUpperCase(),
          riskProbability: risk.riskProbability.toUpperCase(),
          riskStatus: RiskStatusEnum.ESCALATED,
          updatedAt: new Date().toISOString(),
        },
        {
          assessmentId: { eq: risk?.assessmentId },
        }
      );
      this.toastrService.success('Risk Escalated Successfully!');
      return true;
    } catch (e: any) {
      console.log(e);
      this.toastrService.error('Could not escalate risk');
      return false;
    }
  }

  async deEscalateRisk(risk: any): Promise<void> {
    try {
      await this.customApi.UpdateRisk(
        {
          id: risk.id,
          isEscalated: false,
          riskStatus: RiskStatusEnum.OPEN,
          updatedAt: new Date().toISOString(),
        },
        {
          assessmentId: { eq: risk?.assessmentId },
        }
      );
    } catch (e: any) {
      console.log(e);
      this.toastrService.error('Could not de-escalate risk');
    }
  }

  /**
   *
   */
  /**
   * gets current assigned risk framework
   * @returns riskFrameWorks
   * @param assessmentId
   */

  getQuestionsByControlId(id: string): any {
    const control = this.controlsList[id];
    if (control && control.questions) {
      return control.questions.map((item: any) => ({
        ...item,
        controlName: control.name,
        controlLabel: control.label,
      }));
    } else {
      return {};
    }
  }

  getControlNameById(id: string): string {
    const control = this.controlsList[id];
    if (control && control.name) {
      return control.name + ' : ' + control.label + '    ';
    } else {
      return '';
    }
  }

  getControlName(id: string): any {
    const control = this.controlsList[id];
    if (control && control.name) {
      return {
        id: control.name,
        name: control.label,
      };
    } else {
      return {};
    }
  }

  // Creating tags in risk-register service as we don't have any component and service of tags.
  async addNewTagsinDb(
    newTagsList: any,
    targetId: string = '_Global',
    assessmentFrameworkKey?: string,
    referenceId?: string,
    targetName: string = '_Global',
    createNormalTag: boolean = true
  ): Promise<any> {
    const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    if (entityId) {
      const newlyCreatedTags: any[] = [];
      const createTagsPromises: any[] = [];

      // 1. First check if the tag is already exist in the parent list or not.
      if (newTagsList?.length > 0) {
        for (const tag of newTagsList) {
          if (!UtilsService.isEmpty(tag.name)) {
            if (createNormalTag) {
              // creating the normal tag.
              const normalTagObj = {
                name: tag.name,
                entityId,
                domain: this.domain,
                targetId,
                assessmentFrameworkKey,
                referenceId,
                questionRefId: null,
                type: tag?.type || LabelType.RISK,
                parentTagId: tag?.id || null,
                targetName,
              };
              createTagsPromises.push(this.rootEntityContextService.createTag(normalTagObj));
            }
          }
        }
      }

      if (createTagsPromises.length) {
        const createdTags = await Promise.all(createTagsPromises);
        newlyCreatedTags.push(...createdTags);
        // Keep the per-target cache in sync with the new DB rows so that re-entering
        // DLM mode (or expanding the risk later) doesn't append duplicates when
        // fetchAllTagsAgainstRootEntity dedupes by tag.id.
        if (targetId && targetId !== '_Global') {
          const existing = this.allTagsAgainstRootEntity[targetId] || [];
          const existingIds = new Set(existing.map((t: any) => t.id));
          const toAppend = createdTags.filter((t: any) => t?.id && !existingIds.has(t.id));
          this.allTagsAgainstRootEntity[targetId] = [...existing, ...toAppend];
        }
      }
      return newlyCreatedTags;
    }
  }

  // delete tag by id
  async deleteTagFromRisk(id: any): Promise<void> {
    try {
      await this.rootEntityContextService.deleteTag(id, this.rootEntityId);
      // Drop the deleted tag from the per-target cache so subsequent reads don't return stale rows.
      Object.keys(this.allTagsAgainstRootEntity).forEach((targetId: string) => {
        const list = this.allTagsAgainstRootEntity[targetId];
        if (Array.isArray(list)) {
          this.allTagsAgainstRootEntity[targetId] = list.filter((t: any) => t?.id !== id);
        }
      });
    } catch (e: any) {
      console.log('Error - deleteTagFromQuestion: ', e);
    }
  }

  async addNewCategoriesinDb(
    newCategoriesList: any,
    targetId: string = '_Global',
    targetName: string = '_Global',
    createNormalCategory: boolean = true
  ): Promise<any> {
    const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    if (entityId) {
      const newlyCreatedCategories: any[] = [];
      const createCategoriesPromises: any[] = [];

      // 1. First check if the category is already exist in the parent list or not.
      if (newCategoriesList?.length > 0) {
        for (const category of newCategoriesList) {
          if (!UtilsService.isEmpty(category)) {
            if (createNormalCategory) {
              // creating the normal category.
              const normalCategoryObj = {
                type: category.type || LabelType.RISK,
                targetId,
                name: category.name,
                entityId,
                categoryName: category.name,
                categoryId: category.id,
                targetName,
              };
              createCategoriesPromises.push(this.rootEntityContextService.createCategory(normalCategoryObj));
            }
          }
        }
      }

      if (createCategoriesPromises.length) {
        const createdCategories = await Promise.all(createCategoriesPromises);
        newlyCreatedCategories.push(...createdCategories);
        // Keep the per-target cache in sync with the new DB rows so that re-entering
        // DLM mode (or expanding the risk later) doesn't append duplicates when
        // fetchAllCategoriesAgainstRootEntity dedupes by category.id.
        if (targetId && targetId !== '_Global') {
          const existing = this.allCategoriesAgainstRootEntity[targetId] || [];
          const existingIds = new Set(existing.map((c: any) => c.id));
          const toAppend = createdCategories.filter((c: any) => c?.id && !existingIds.has(c.id));
          this.allCategoriesAgainstRootEntity[targetId] = [...existing, ...toAppend];
        }
      }
      return newlyCreatedCategories;
    }
  }

  async deleteCategoryFromRisk(id: string): Promise<void> {
    try {
      await this.rootEntityContextService.deleteCategory(id, this.rootEntityId);
      // Drop the deleted category from the per-target cache so subsequent reads don't return stale rows.
      Object.keys(this.allCategoriesAgainstRootEntity).forEach((targetId: string) => {
        const list = this.allCategoriesAgainstRootEntity[targetId];
        if (Array.isArray(list)) {
          this.allCategoriesAgainstRootEntity[targetId] = list.filter((c: any) => c?.id !== id);
        }
      });
    } catch (e: any) {
      console.log('Error - deleteCategoryFromRisk: ', e);
    }
  }

  async getControlsList(entity: any, _populateAllControlsList: boolean = false): Promise<void> {
    void _populateAllControlsList;

    const assessmentId = entity?.activeAssessmentId;
    if (!assessmentId) {
      return;
    }

    // In-flight deduplication: if controls are already being loaded for this assessmentId,
    // wait for and return that existing fetch instead of making a duplicate request
    if (this._controlsLoadingInFlight[assessmentId]) {
      return this._controlsLoadingInFlight[assessmentId];
    }

    // Mark as in-flight before starting the fetch
    const fetchPromise = (async () => {
      try {
        const riskFrameWork = entity?.activeAssessment?.standardFrameworkList?.items?.find(
          (item: any) => item.type === StandardType.RISK_FRAMEWORK && !item.archived
        );

        const results = await this.frameworkTreeService.getControlsFromAssessment(
          assessmentId,
          riskFrameWork // framework
        );

        // adding description of questions against their controls
        let controlName = '';
        let index = 0;
        const tempControls: any[] = [];

        if (results && results?.controls) {
          results?.controls?.forEach((control: any) => {
            if (control.name.toLowerCase() === controlName.toLowerCase()) {
              index = index + 1;
            } else {
              index = 0;
              controlName = control.name;
            }

            if (control?.questions && control?.questions.length) {
              control?.questions.forEach((question: any) => {
                tempControls.push({
                  id: control.name,
                  controlId: control.id,
                  name: control.label,
                  questionDesc: question.description,
                  impact: false,
                  probability: false,
                  weight: 1,
                  description: question.name,
                  smartMappingId: question.smartMappingId,
                  isAI: false,
                  selected: false,
                  value: false,
                });
              });
            }

            this.controlsList[control.id] = control;
          });

          // creating a mapper for controls of risk framework so that on selecting different sub-entities
          // time complexity remains constant
          if (!this.controlsAgainstSubEntity) {
            this.controlsAgainstSubEntity = {};
          }
          if (!this.controlsAgainstSubEntity[assessmentId]) {
            this.controlsAgainstSubEntity[assessmentId] = tempControls;
          }
        }
      } catch (err: any) {
        this.toastrService.error('Error - getControlsList: ', err);
        throw err;
      } finally {
        // Clean up the in-flight promise after completion
        delete this._controlsLoadingInFlight[assessmentId];
      }
    })();

    // Store the promise before awaiting so concurrent calls can find it
    this._controlsLoadingInFlight[assessmentId] = fetchPromise;
    await fetchPromise;
  }

  /**
   * Ensure controls are loaded for the given assessment.
   * If already loaded, returns immediately; if loading, waits for completion.
   * Useful as a guard before trying to access controlsAgainstSubEntity[assessmentId].
   */
  async ensureControlsLoaded(entity: any): Promise<void> {
    const assessmentId = entity?.activeAssessmentId;
    if (!assessmentId) {
      return;
    }

    // If controls already cached, no fetch needed
    if (this.controlsAgainstSubEntity?.[assessmentId]) {
      return;
    }

    // Otherwise load them (in-flight dedup handles parallel calls)
    await this.getControlsList(entity);
  }

  setControlsList(activeAssessmentId: string): void {
    try {
      this.controlsList = {};
      this.controlsList = this.controlsAgainstSubEntity[activeAssessmentId];
    } catch (err: any) {
      this.toastrService.error('Error - setControlsList:', err);
    }
  }

  async getRisksList(
    assessmentId: string,
    _isMorethanSeven: boolean = false,
    _removeCacheData: boolean = false
  ): Promise<any> {
    void _isMorethanSeven;
    void _removeCacheData;
    if (!this.riskTableData.allRisk[assessmentId]) {
      // Source risks from the single RootEntityContextService cache (kept live by AppSync
      // subscriptions) instead of an independent RiskByAssessmentId query, so this service and the
      // risk register never diverge on the same assessment's risks.
      const risks = await this.rootEntityContextService.getRisksByAssessmentIds([assessmentId]);
      if (!this.riskTableData.allRisk[assessmentId]) {
        this.riskTableDataHandling(risks, false, assessmentId);
      }
      return risks;
    } else {
      return this.riskTableData.allRisk[assessmentId];
    }
  }

  // ------------------------------------------------- RISK QUESTION UPDATE FUNCTIONS ------------------------------------------------------

  // ----------------------------------------- Remediation Question Updates Functions ---------------------------------------

  /**
   * @param currentUser is type of user object ( The Current User logged into the system )
   * @param questionsData is Type of object that contains keys= assessmentIds and values as list of Questions objects
   * This function Basically process the List of Questions against a assessment Id and Update the Risk against each Question
   */
  async deduceAnswerRiskRemediation(currentUser: any, questionsData: any, riskAction?: RiskAction): Promise<void> {
    // here we process the Data on
    const keys = Object.keys(questionsData);
    this.currentUser = currentUser;
    for (const key of keys) {
      if (!this.controlsAgainstSubEntity[key]) {
        // here key is a assessment Id (key=assessmentId)
        await this.getControlsList(key);
      } else {
        this.setControlsList(key);
      }
      // RECS-backed risks read (RootEntityContextService cache, kept live by AppSync subscriptions
      // and the create/update/delete write-throughs) instead of a direct RiskByAssessmentId query.
      const dbRisks: any[] = (await this.rootEntityContextService.getRisksByAssessmentIds([key])) || [];
      // move the Risk->Questions list here
      const riskQuestionsData: any[] = [];
      // questionsData[key] is a list of questions against a assessmentId ( as key=assessmentId )
      for (const question of questionsData[key]) {
        const manualRisks = dbRisks.filter((risk: any) => risk.type === RiskTypeEnum.MANUAL);
        let questionRisks = await this.getRisksOfAQuestion(question);
        if (manualRisks && manualRisks.length && this.getManualRisksOfAQuestion(manualRisks, question)) {
          questionRisks = [...questionRisks, ...(this.getManualRisksOfAQuestion(manualRisks, question) || [])];
        }
        // Process all the Risks
        const isPassed = this.getPassedValue(question);
        questionRisks.map((risk: any) => {
          if (riskQuestionsData.length) {
            const index = riskQuestionsData.findIndex((item: any) => {
              if (item.risk.RiskID.trim().toLowerCase() === risk.RiskID.trim().toLowerCase()) {
                return item;
              }
            });
            if (index >= 0) {
              riskQuestionsData[index].questions.push({ ...question, isPassed, action: riskAction });
            } else {
              riskQuestionsData.push({ risk, questions: [{ ...question, isPassed, action: riskAction }] });
            }
          } else {
            riskQuestionsData.push({ risk, questions: [{ ...question, isPassed, action: riskAction }] });
          }
        });
      }
      await this.addRemoveRiskRemediationQuestions(riskQuestionsData, key, dbRisks);
    }
  }

  /**
   *
   * @param riskQuestions The risk that have the attached questions
   * @param assessment assessment ID
   * @param dbRisks The existing risk in Dynamo DB
   * This functionality updates questions coming from Remediation
   */
  async addRemoveRiskRemediationQuestions(riskQuestions: any, assessment: string, dbRisks: any): Promise<void> {
    // here we Received
    let isUpdate = false;
    const updateRisks: any[] = [];
    const createRisks: any[] = [];
    // loop the RiskQuestions ... Risk 1,2,3...
    riskQuestions.map((riskQuestion: any) => {
      let cacheRisk: any;
      const rsk = riskQuestion.risk;
      // loop through each question in RiskQuestion ...  Rn->Q1,Q2,Q3....
      riskQuestion.questions.map((question: any) => {
        // if not passed then add the risk affiliated with that question
        // first find if those risks already exists
        if (cacheRisk) {
          cacheRisk.hidden = false;
          // if already controls list exist , simply add it.
          if (cacheRisk.controlIds && cacheRisk.controlIds.length) {
            // if the control ID is already in risk , then don't update. else add it.
            if (!cacheRisk.controlIds.includes(question.parentId)) {
              cacheRisk.controlIds.push(question.parentId);
            }
          } else {
            // if it is empty, just add control id
            cacheRisk.controlIds = [];
            cacheRisk.controlIds.push(question.parentId);
          }
          // adjusting score history and calculating residual risk
          cacheRisk.scoreHistory = this.updateScoreHistory(false, question, cacheRisk.scoreHistory, question.action);
          // check score history, if all scores 10 then mitigate risk
          this.mitigateRiskBaseOnScoreHistory(cacheRisk);

          const calculation = this.calculateResidual(cacheRisk.scoreHistory, cacheRisk);

          cacheRisk.effectiveness = calculation.effectiveness;
          cacheRisk.residualImpact = UtilsService.getImpactEnumValue(calculation.residualImpact);
          cacheRisk.residualProbability = UtilsService.getProbabilityEnumValue(calculation.residualProbability);
          cacheRisk.residualImpactValue = calculation.residualImpact;
          cacheRisk.residualProbabilityValue = calculation.residualProbability;
          cacheRisk.residualRisk = calculation.residualRisk;
        } else if (dbRisks && dbRisks.length) {
          if (rsk.type === RiskTypeEnum.MANUAL) {
            cacheRisk = dbRisks.find((rsk1: any) => {
              // finding the risk , if it doesn't exist , by default we won't add it.
              if (rsk1.id === rsk.id && rsk1.idTitle.toLowerCase() === rsk.RiskID.toLowerCase()) {
                // making sure if the control id array has something in it.
                isUpdate = true;
                return rsk1;
              }
            });
          } else {
            cacheRisk = dbRisks.find((rsk1: any) => {
              // finding the risk , if it doesn't exist , by default we won't add it.
              if (
                rsk1.idTitle.toLowerCase() === rsk.RiskID.toLowerCase() &&
                rsk1.riskCategory &&
                rsk.riskCategory &&
                rsk1.riskCategory.toLowerCase() === rsk.riskCategory.toLowerCase()
              ) {
                // making sure if the control id array has something in it.
                isUpdate = true;
                return rsk1;
              }
            });
          }
          if (cacheRisk) {
            cacheRisk.hidden = false;
            // if already controls list exist , simply add it.
            if (cacheRisk.controlIds && cacheRisk.controlIds.length) {
              // if the control ID is already in risk , then don't update. else add it.
              if (!cacheRisk.controlIds.includes(question.parentId)) {
                cacheRisk.controlIds.push(question.parentId);
              }
            } else {
              // if it is empty, just add control id
              cacheRisk.controlIds = [];
              cacheRisk.controlIds.push(question.parentId);
            }
            // adjusting score history and calculating residual risk
            cacheRisk.scoreHistory = this.updateScoreHistory(false, question, cacheRisk.scoreHistory, question.action);
            // check score history, if all scores 10 then mitigate risk

            this.mitigateRiskBaseOnScoreHistory(cacheRisk);

            const calculation = this.calculateResidual(cacheRisk.scoreHistory, cacheRisk);

            cacheRisk.effectiveness = calculation.effectiveness;
            cacheRisk.residualImpact = UtilsService.getImpactEnumValue(calculation.residualImpact);
            cacheRisk.residualProbability = UtilsService.getProbabilityEnumValue(calculation.residualProbability);
            cacheRisk.residualImpactValue = calculation.residualImpact;
            cacheRisk.residualProbabilityValue = calculation.residualProbability;
            cacheRisk.residualRisk = calculation.residualRisk;
          } else {
            cacheRisk = this.createNewRiskObject(rsk, question, assessment);
            isUpdate = false;
          }
        } else {
          // this will add a current Risk in the if the Risk is not in dbRisks
          cacheRisk = this.createNewRiskObject(rsk, question, assessment);
          isUpdate = false;
        }
      });

      if (isUpdate) {
        updateRisks.push(cacheRisk);
      } else {
        createRisks.push(cacheRisk);
      }
    });

    if (updateRisks && updateRisks.length) {
      const promises: any[] = [];
      updateRisks.forEach((risk: any) => {
        promises.push(this.updateRisk(risk, true, false));
      });
      await Promise.all(promises);
    }

    if (createRisks && createRisks.length) {
      const promises: any[] = [];
      createRisks.forEach((newRisk: any) => {
        promises.push(this.createNewRisk(newRisk));
      });
      await Promise.all(promises);
    }

    if (UtilsService.isBnB || UtilsService.isBnBCyberSite) {
      const updatingRisks: any[] = [...createRisks, ...updateRisks];
      if (updatingRisks && updatingRisks.length) {
        await this.entityService.updateResidualScore(updatingRisks[0].entityId, true, null);
      }
    }
  }

  // ----------------------------------- Collection Question Updates Functions --------------------------------------------

  manualRiskUpdateCollection(question: any, risks: any, updatedRisks: any, isRemoveScoreHistory: boolean = true) {
    risks
      .filter((risk: any) => risk.type === RiskTypeEnum.MANUAL)
      .forEach((manualRisk: any) => {
        let rskObject = null;
        if (
          manualRisk.controlIds &&
          manualRisk.controlIds.length &&
          manualRisk.controlIds.includes(question.parentId)
        ) {
          manualRisk.scoreHistory = this.updateScoreHistory(isRemoveScoreHistory, question, manualRisk.scoreHistory);

          const calculation = this.calculateResidual(manualRisk.scoreHistory, manualRisk);

          manualRisk.effectiveness = calculation.effectiveness;
          manualRisk.residualImpact = UtilsService.getImpactEnumValue(calculation.residualImpact);
          manualRisk.residualProbability = UtilsService.getProbabilityEnumValue(calculation.residualProbability);
          manualRisk.residualImpactValue = calculation.residualImpact;
          manualRisk.residualProbabilityValue = calculation.residualProbability;
          manualRisk.residualRisk = calculation.residualRisk;
          manualRisk.hidden = !(manualRisk.scoreHistory && manualRisk.scoreHistory.length);
          rskObject = manualRisk;
        }

        if (rskObject) {
          updatedRisks.push(rskObject);
        }
      });
  }

  /**
   * @param currentUser is type of user object ( The Current User logged into the system )
   * @param question is type of question object
   * This function basically update the Risk Against the a Question is Answered
   */
  async deduceAnswerRisk(currentUser: any, question: any, currentUserRole: any): Promise<void> {
    const assessment = question.assessmentIds[0];
    this.currentUser = currentUser;
    const frameName = FrameworkTreeService.getFrameworkKey();
    // add all checks
    // 1)- is NIST CSF
    // 2)- is from 1st Party Collection
    // 3)- check role based / impact of answer - is dependant of isPassed Value.
    // Note :- Removed the role case for Participant - only added check for Vendor Role only
    if (
      question.frameworkName === frameName &&
      this.router.url.includes('first-party') &&
      // currentUserRole.name.toLowerCase() !== RoleEnum.PARTICIPANT.toLowerCase() &&
      currentUserRole.name !== RoleEnum.VENDOR.toLowerCase()
    ) {
      // passed will be cal
      const isPassed = this.getPassedValue(question);
      try {
        // await this.getControlsList(assessment);
        // RECS-backed risks read — same single cache the rest of the risk register uses.
        const dbRisks: any[] = (await this.rootEntityContextService.getRisksByAssessmentIds([assessment])) || [];
        const question2 = { ...question, isPassed };
        this.addRemoveQuestionRisk(question2, assessment, dbRisks);
      } catch (e: any) {
        console.log(e);
      }
    }
  }

  /**
   *
   * @param question The question object
   * @param assessment the assessment ID
   * @param dbRisks The existing risks in that assessment table
   * This functionality updates questions coming from Collection
   */
  public async addRemoveQuestionRisk(question: any, assessment: string, dbRisks: any): Promise<void> {
    // initial setup to retrieve affiliated risks
    const riskList = await this.getStaticRisks(question.frameworkName);
    const controls = await this.getMappedRisks(question.frameworkName);

    let getControlWithRisks: any;
    if (
      UtilsService.isDefined(question) &&
      UtilsService.isDefined(question.controlName) &&
      UtilsService.isDefined(question.controlLabel) &&
      UtilsService.isDefined(question.name)
    ) {
      getControlWithRisks = controls?.mappedRisks.find((con: any) => {
        const conName = con?.controlName ? con.controlName.toString() : '';
        if (
          conName.toLowerCase().trim() === question.controlName.toLowerCase().trim() &&
          con.label.toLowerCase().trim() === question.controlLabel.toLowerCase().trim() &&
          question.name.toLowerCase().trim() === con.questionTitle.toLowerCase().trim()
        ) {
          return con;
        }
      });
    } else {
      return;
    }
    let riskAttached: any[] = [];
    // check getControlWithRisks is defined or not first
    if (UtilsService.isDefined(getControlWithRisks)) {
      // getting attached risks
      riskAttached = getControlWithRisks.riskID ? getControlWithRisks.riskID.split(',') : [];
    } else {
      // Just Exist the function there is no risk against this question
      return;
    }

    const riskAffiliated: any[] = [];
    riskAttached.forEach((risk: any) => {
      riskAffiliated.push(risk.trim());
    });

    // getting risk detailed object
    const risksData: any[] = [];
    riskList?.risks.forEach((rsk: any) => {
      if (riskAffiliated.includes(rsk.RiskID?.trim())) {
        risksData.push(rsk);
      }
    });

    const updateRisks: any[] = [];
    const newRisks: any[] = [];
    const promises: any[] = [];
    // if not passed then add the risk affiliated with that question
    // first find if those risks already exists
    if (dbRisks && dbRisks.length) {
      risksData.forEach((rsk: any) => {
        // finding existing risk if found , else create new one.
        const rskObject = dbRisks.find((rsk1: any) => {
          if (
            rsk1.idTitle.toLowerCase() === rsk.RiskID.toLowerCase() &&
            rsk1.riskCategory &&
            rsk.riskCategory &&
            rsk1.riskCategory.toLowerCase() === rsk.riskCategory.toLowerCase()
          ) {
            // add control ID and make it non - hidden.
            // if already controls list exist , simply add it.
            if (rsk1.controlIds && rsk1.controlIds.length) {
              // if the control ID is already in risk , then don't update. else add it.
              if (!rsk1.controlIds.includes(question.parentId)) {
                rsk1.controlIds.push(question.parentId);
              }
            } else {
              // if it is empty, just add control id
              rsk1.controlIds = [];
              // calculate residual -------------------------------------------------------------------------------------
              rsk1.controlIds.push(question.parentId);
            }
            rsk1.scoreHistory = this.updateScoreHistory(false, question, rsk1.scoreHistory);

            const calculation = this.calculateResidual(rsk1.scoreHistory, rsk1);

            rsk1.effectiveness = calculation.effectiveness;
            rsk1.residualImpact = UtilsService.getImpactEnumValue(calculation.residualImpact);
            rsk1.residualProbability = UtilsService.getProbabilityEnumValue(calculation.residualProbability);
            rsk1.residualImpactValue = calculation.residualImpact;
            rsk1.residualProbabilityValue = calculation.residualProbability;
            rsk1.residualRisk = calculation.residualRisk;

            rsk1.hidden = false;

            return rsk1;
          }
        });
        // check if existing risk needs updating.
        if (rskObject) {
          updateRisks.push(rskObject);

          // check score history, if all scores 10 then mitigate risk or if it is Semi-Auto Risk having
          // only Manual Tasks , then mitigate it
          this.mitigateRiskBaseOnScoreHistory(rskObject);
        } else if (!rskObject) {
          // if for some reason the risk does not exist we must add new risk with control id.
          const createdRisk = this.createNewRiskObject(rsk, question, assessment);
          newRisks.push(createdRisk);

          // check score history, if all scores 10 then mitigate risk or if it is Semi-Auto Risk having
          // only Manual Tasks , then mitigate it
          this.mitigateRiskBaseOnScoreHistory(createdRisk);
        }
      });
      this.manualRiskUpdateCollection(question, dbRisks, updateRisks, false);
      if (updateRisks && updateRisks.length) {
        // --------------------------------------------------------------------------------API CALL
        updateRisks.forEach((risk: any) => {
          promises.push(this.updateRisk(risk, true, false));
        });
      }
      if (newRisks && newRisks.length) {
        // ---------------------------------------------------------------------------------API CALL
        newRisks.forEach((newRisk: any) => {
          promises.push(this.createNewRisk(newRisk, true));
        });
      }
      if (UtilsService.isBnB || UtilsService.isBnBCyberSite) {
        await this.entityService.updateResidualScore(question.rootId, true, null);
      }
    } else {
      const newRisksObj: any[] = [];
      risksData.forEach((rsk: any) => {
        const newRiskObject = this.createNewRiskObject(rsk, question, assessment);
        newRisksObj.push(newRiskObject);
        this.mitigateRiskBaseOnScoreHistory(newRiskObject);
      });
      // if they don't exist , add them
      // -----------------------------------------------------------------------------------API CALL
      newRisksObj.forEach((newRisk: any) => {
        promises.push(this.createNewRisk(newRisk, true));
      });

      if (UtilsService.isBnB || UtilsService.isBnBCyberSite) {
        await this.entityService.updateResidualScore(question.rootId, true, null);
      }
    }
    if (promises && promises.length) {
      await Promise.all(promises);
    }
  }

  // --------------------------------------------- Helper functions -------------------------------------------------

  /**
   *
   * @param rsk The risk containing it's details
   * @param question The question related to that risk
   * @param assessment the Assessment ID
   * @returns The complete score object to store in DB
   */
  createNewRiskObject(rsk: any, question: any, assessment: string): any {
    const scoreList: RiskQuestionScoreInput[] = [this.createScoreHistory(question, question.action)];
    this.mitigateRiskBaseOnScoreHistory(rsk);
    const newRisk = {
      id: `${assessment}#${rsk.RiskID.trim()}`,
      riskTitle: rsk.riskTitle.trim(),
      relatedAssets: rsk.relatedAssets ? rsk.relatedAssets.split(', ') : [],
      entityId: question.rootId,
      subEntityId: this.controlsList[question.parentId]?.entityId,
      idTitle: rsk.RiskID.trim(),
      assessmentId: assessment,
      type: RiskTypeEnum.AUTOMATIC,
      riskImpact:
        rsk.riskImpact && rsk.riskImpact.toLowerCase() === 'critical' ? 'CRITICAL' : rsk.riskImpact.toUpperCase(),
      riskProbability:
        rsk.riskProbability && rsk.riskProbability.toLowerCase() === 'critical'
          ? 'CRITICAL'
          : rsk.riskProbability.toUpperCase(),
      riskPriority: UtilsService.getRandomEnumVal(RiskPriorityEnum),
      disable: false,
      residualRisk: 0,
      residualImpact: rsk.riskImpact,
      residualProbability: rsk.riskProbability,
      residualImpactValue: rsk.residualImpactValue,
      residualProbabilityValue: rsk.residualProbabilityValue,
      inherentRisk: rsk.inherentRisk,
      riskStatus: rsk.riskStatus ? rsk.riskStatus.toUpperCase() : rsk.riskStatus,
      costToRemediateType: 'LOW',
      controlIds: [this.controlsList[question.parentId]?.id],
      riskDescription: rsk.riskDescription,
      notes: rsk.notes,
      riskCategory: rsk.riskCategory,
      scoreHistory: scoreList,
      sensitivity: 1,
      effectiveness: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const inherent =
      (UtilsService.getLabelValueNum(newRisk.riskImpact) + UtilsService.getLabelValueNum(newRisk.riskProbability)) / 2;
    newRisk.inherentRisk = parseFloat(inherent.toFixed(1));

    const calculation = this.calculateResidual(scoreList, newRisk);

    newRisk.effectiveness = calculation.effectiveness;
    newRisk.residualImpact = UtilsService.getImpactEnumValue(calculation.residualImpact);
    newRisk.residualProbability = UtilsService.getProbabilityEnumValue(calculation.residualProbability);
    newRisk.residualImpactValue = calculation.residualImpact;
    newRisk.residualProbabilityValue = calculation.residualProbability;
    newRisk.residualRisk = calculation.residualRisk;

    return newRisk;
  }

  /**
   * @param value the value is number between 0-10
   * @param isInherent Does the list contain Critical Keyword
   * @returns The String value equivalent of the ranged value 0-10
   */
  turnIntToStr(value: number, isInherent: boolean): string {
    const newVal = value * 10;
    const strVal = UtilsService.getLabelValue(newVal, isInherent ? 'impact' : 'probability');
    return UtilsService.capitalizeFirstLetter(strVal);
  }

  /**
   *
   * @param question the question of the recent update
   * @returns the object containing scoreHistoryObject
   */
  createScoreHistory(question: any, riskAction: any): RiskQuestionScoreInput {
    return {
      controlId: question.parentId || question.controlId,
      questionOrder: question.order,
      remediationStatus: riskAction,
      score: question.isPassed,
      left: question.left,
      right: question.right,
    };
  }

  updateScoreHistory(
    removed: boolean,
    question: any,
    scoreHist: RiskQuestionScoreInput[],
    riskAction?: RiskAction
  ): RiskQuestionScoreInput[] {
    let updatedHistory: RiskQuestionScoreInput[] = [];
    if (removed) {
      // removing the instance of that score history object
      if (scoreHist && scoreHist.length) {
        const index = this.findQuestionInScoreHist(question, scoreHist);
        if (index > -1) {
          // the instance is found and simply removing it
          scoreHist.splice(index, 1);
          // if there is anything left , return the scoreHist or return []
          if (scoreHist.length) {
            updatedHistory = JSON.parse(JSON.stringify(scoreHist));
            return updatedHistory;
          } else {
            return updatedHistory;
          }
        }
      } else {
        return updatedHistory;
      }
    } else {
      const riskStatus = UtilsService.isDefined(riskAction) ? riskAction : 'NA';
      // adding the new score hist object
      if (scoreHist && scoreHist.length) {
        const index = this.findQuestionInScoreHist(question, scoreHist);
        if (index > -1) {
          // if found , find the instance and update its score
          scoreHist[index].score = question.isPassed;
          // if riskAction is defined then set remediationStatus = riskAction, if not then 'NA'
          scoreHist[index].remediationStatus = riskStatus;
          updatedHistory = JSON.parse(JSON.stringify(scoreHist));
          return updatedHistory;
        } else {
          // if there is not instance , then simply add into existing score hist
          scoreHist.push(this.createScoreHistory(question, riskStatus));
          updatedHistory = JSON.parse(JSON.stringify(scoreHist));
          return updatedHistory;
        }
      } else {
        // score hist is empty , add the only 1 here and return it.
        updatedHistory.push(this.createScoreHistory(question, riskStatus));
        return updatedHistory;
      }
    }
    return updatedHistory;
  }

  calculateFinancialResidual(rsk: any): number {
    let residualFinancial = 0;
    if (rsk?.primaryLoss) {
      // ! Old Financial Residual Calculation
      // residualFinancial = rsk.primaryLoss * (1 - (rsk.sensitivity * rsk.residualRisk) / 10);
      //* New Financial Residual Calculation
      const effectiveness = (rsk?.effectiveness || 0) / 10;
      residualFinancial = rsk.primaryLoss - effectiveness * rsk.primaryLoss;
      const residualFinancialFloor = RISK_RESIDUAL_FLOOR * rsk.primaryLoss;
      residualFinancial = Math.max(residualFinancial, residualFinancialFloor);
    }
    return residualFinancial;
  }

  /**
   *
   * @param scoreHist The score History List object
   * @returns the residual risk calculation
   */
  calculateResidual(scoreHist: RiskQuestionScoreInput[], riskObj: any): any {
    const SCORE_ENUM = RISK_RESIDUAL_SCORE_ENUM;
    let effectiveness = 0;
    let residualRisk = 0;
    let residualImpact = 0;
    let residualProbability = 0;

    let inherentScore =
      (UtilsService.getLabelValueNum(riskObj.riskImpact) + UtilsService.getLabelValueNum(riskObj.riskProbability)) / 2;
    inherentScore = parseFloat(inherentScore.toFixed(2));

    if (scoreHist && scoreHist.length) {
      // if there are score objects , calculate the score
      let impactWithWeight = 0;
      let probabilityWithWeight = 0;
      let totalWeight = 0;
      let totalImpactWithWeight = 0;
      let totalProbabilityWithWeight = 0;

      scoreHist.forEach(({ score, weight, impact, probability }: any) => {
        //* Removing the N/A Questions
        if (score === SCORE_ENUM.NA || weight === SCORE_ENUM.NA) {
          return;
        }

        //* Not Calculating Total Impact and probability for NO answers and unanswered controls
        if (score && score !== SCORE_ENUM.NO && score !== -1) {
          impactWithWeight += weight * score * (impact ? 1 : 0);
          probabilityWithWeight += weight * score * (probability ? 1 : 0);
        }

        if (weight) {
          totalWeight += weight;
          totalImpactWithWeight += impact ? weight : 0;
          totalProbabilityWithWeight += probability ? weight : 0;
        }
      });

      if (totalWeight || totalWeight === 0) {
        const finalImpactWithWeight = totalImpactWithWeight !== 0 ? impactWithWeight / totalImpactWithWeight : 0;
        const finalProbabilityWithWeight =
          totalProbabilityWithWeight !== 0 ? probabilityWithWeight / totalProbabilityWithWeight : 0;

        residualImpact =
          UtilsService.getLabelValueNum(riskObj.riskImpact) * (1 - (riskObj.sensitivity * finalImpactWithWeight) / 10);
        residualProbability =
          UtilsService.getLabelValueNum(riskObj.riskProbability) *
          (1 - (riskObj.sensitivity * finalProbabilityWithWeight) / 10);
        residualRisk = (residualImpact + residualProbability) / 2;
      }
    } else {
      // if there is none - simply score would be 10
      residualRisk = inherentScore;
      residualRisk = UtilsService.getLabelValueNum(riskObj.riskImpact);
      residualProbability = UtilsService.getLabelValueNum(riskObj.riskProbability);
    }

    //* New Effectiveness
    effectiveness = ((inherentScore - residualRisk) / inherentScore) * 10;

    // boundary cases
    residualRisk = UtilsService.fixBoundaries(residualRisk);
    effectiveness = UtilsService.fixBoundaries(effectiveness);
    residualImpact = UtilsService.fixBoundaries(residualImpact);
    residualProbability = UtilsService.fixBoundaries(residualProbability);

    // Parse Float and for one digit
    residualRisk = parseFloat(residualRisk.toFixed(2));
    effectiveness = parseFloat(effectiveness.toFixed(1));
    residualImpact = parseFloat(residualImpact.toFixed(2));
    residualProbability = parseFloat(residualProbability.toFixed(2));

    return {
      residualRisk,
      effectiveness,
      residualImpact,
      residualProbability,
    };
  }
  /**
   *
   * @param question the question object
   * @param scoreHist the score history object of the risk
   * @returns the index position of the question in score history
   */
  findQuestionInScoreHist(question: any, scoreHist: RiskQuestionScoreInput[]): number {
    const index = scoreHist.findIndex(
      (score: any) =>
        score.controlId === question.parentId &&
        score.questionOrder === question.order &&
        score.left === question.left &&
        score.right === question.right
    );
    return index;
  }

  /**
   *
   * @param question The question object that contains the answer score
   * @returns The score value of that question
   */
  getPassedValue(question: any): number {
    // if question object contains settings then it means the payload is from collection
    if (question.settings) {
      if (question.settings.isCollaborative) {
        if (question.answers.length > 1) {
          // get the Answer by latest date
          const latestAnswer = question.answers.reduce((a: any, b: any) => (a.createdAt > b.createdAt ? a : b));
          question.answers = [latestAnswer];
        }
      } else {
        // get Latest Answer on the Current UserID
        const latestAnswer = question.answers.find((item: any) => item.userId === this.currentUser?.id);
        question.answers = [latestAnswer];
      }
      if (question.answers && question.answers[0] && UtilsService.isDefined(question.answers[0].answer)) {
        // if question's answered id (Not applicable or answer=0) . we return isPassed =10
        if (question.answers[0].answer === 0) {
          return 10;
        } else if (question.answers[0].answer < 0) {
          return 0;
        }
        return question.answers[0].answer;
      }
    } else {
      // the payload is from remediation
      // if question's answered id (Not applicable or answer=0) . we return isPassed =10
      if (question.score === 0) {
        return 10;
      } else if (question.score < 0) {
        return 0;
      }
      return question.score;
    }
    return 0;
  }

  /**
   * @param question is type Question object
   * @returns is type of list of Risks against a question
   */
  async getRisksOfAQuestion(question: any): Promise<any> {
    const riskList = await this.getStaticRisks(question.frameworkName);
    const controls = await this.getMappedRisks(question.frameworkName);
    // getting controls with mapped risks
    const controlName = question.controlName
      ? question.controlName
      : question.controlsConnected
        ? question.controlsConnected[0].name
        : '';
    const controlLabel = question.controlLabel
      ? question.controlLabel
      : question.controlsConnected
        ? question.controlsConnected[0].label
        : '';
    const getControlWithRisks = controls?.mappedRisks.find((con: any) => {
      return con.controlName === controlName && con.label === controlLabel && question.title === con.questionTitle;
    });
    // getting attached risks
    let riskAttached = null;
    if (UtilsService.isDefined(getControlWithRisks)) {
      // getting attached risks
      riskAttached = getControlWithRisks.riskID ? getControlWithRisks.riskID.split(',') : [];
    } else {
      // Just Exist the function there is no risk against this question
      return;
    }
    const riskAffiliated: any[] = [];
    riskAttached.forEach((risk: any) => {
      riskAffiliated.push(risk.trim());
    });
    // getting risk detailed object
    const risksData: any[] = [];
    riskList?.risks.forEach((rsk: any) => {
      if (riskAffiliated.includes(rsk.RiskID.trim())) {
        risksData.push(rsk);
      }
    });

    return risksData;
  }
  getManualRisksOfAQuestion(risks: any = [], question: any): any[] {
    const risksData: any[] = [];
    risks.forEach((manualRisk: any) => {
      if (manualRisk.controlIds && manualRisk.controlIds.length && manualRisk.controlIds.includes(question.controlId)) {
        risksData.push({
          id: manualRisk.id,
          RiskID: manualRisk.idTitle,
          inherentRisk: manualRisk.inherentRisk,
          relatedAssets: manualRisk.relatedAssets ? manualRisk.relatedAssets.join(',') : '',
          riskCategory: manualRisk.riskCategory,
          riskDescription: manualRisk.riskDescription,
          riskImpact: manualRisk.riskImpact,
          riskProbability: manualRisk.riskProbability,
          riskStatus: manualRisk.riskStatus,
          riskTitle: manualRisk.riskTitle,
          type: manualRisk.type,
        });
      }
    });
    return risksData.length ? risksData : [];
  }

  mitigateRiskBaseOnScoreHistory(risk: any): void {
    if (risk && risk.scoreHistory && !!risk.scoreHistory.length) {
      const scores = risk.scoreHistory.map((sh: any) => sh.score);
      const status = risk.scoreHistory.map((sh: any) => sh.remediationStatus);

      // need to check the total count of questions ,
      // even including those questions as well which haven't been answered.
      let totalQC = this.getRiskTotalQuestionCount(risk.idTitle);
      if (!totalQC) {
        totalQC = risk.scoreHistory.length;
      }
      const scoreTenCount = scores.reduce((m: any, a: any) => {
        if (a === 10) {
          m += 1;
        }
        return m;
      }, 0);
      // commenting this for now
      // const scoreOneCount = scores.reduce((m, a) => {
      //   if (a === 1) {
      //     m += 1;
      //   }
      //   return m;
      // }, 0);
      const acceptedCount = status.reduce((m: any, a: any) => {
        if (a === RiskAction.ACCEPTED) {
          m += 1;
        }
        return m;
      }, 0);
      // commenting this condition for now : (scoreOneCount === risk.scoreHistory.length && acceptedCount === risk.scoreHistory.length)
      // finding 1 score count, if score 1 count equal to scoreHistory length, it means
      // all relevant questions has been accepted
      if (acceptedCount === totalQC) {
        // if all of the tasks are accepted then set the status to accepted
        console.log('risk accepted');
        risk.riskStatus = RiskStatusEnum.ACCEPTED;
      } else if (scoreTenCount === totalQC) {
        // finding 10 score count, if score 10 count equal to scoreHistory length, it means
        // all relevant questions has been answered Yes and all of them are not ignored, change the status to mitigate
        console.log('risk mitigating');
        risk.riskStatus = RiskStatusEnum.MITIGATED;
      } else {
        console.log('risk pending');
        risk.riskStatus = RiskStatusEnum.OPEN;
      }
    }

    // Self Note , not using the totalQC variable for SEMI and MANUAL risks.
    // cause we are checking manual tasks here...in which their total count is dynamic.

    // If a Risk has Manual Tasks ( Only Semi-Auto and Manual Risks have them )
    if (risk && risk.type && risk.type !== RiskTypeEnum.AUTOMATIC) {
      if (risk.riskTasks && risk.riskTasks.length) {
        let closedCount = 0;
        risk.riskTasks.forEach((task: any) => {
          if (task.status === TaskStatusEnum.REMEDIATED) {
            closedCount++;
          }
        });
        // if one of these conditions is True , only the the Manual Tasks affect the risk status
        // 1st if the Risk does not have score history , meaning no auto tasks are created.
        // 2nd if the Risk does have a score history but length is 0 , still same case for 1st condition
        // 3rd if the Risk does have score history and has actual values , we must check if the status is being
        // mitigated , only the Manual Tasks status's are counted.
        if (
          !risk.scoreHistory ||
          (risk.scoreHistory && !risk.scoreHistory.length) ||
          (risk.scoreHistory.length && risk.riskStatus === RiskStatusEnum.MITIGATED)
        ) {
          risk.riskStatus = closedCount === risk.riskTasks.length ? RiskStatusEnum.MITIGATED : RiskStatusEnum.OPEN;
        }
      }
    }
  }

  // Note
  // Create Answer for Question if User never answer it, execute in risk mitigate
  async createAnswerForQuestionOnRiskBasis(question: any, userAssignment: any, userAnswer: any) {
    const frameName = FrameworkTreeService.getFrameworkKey();
    const createAnswer: CreateAnswerInput = {
      id: uuid(),
      assessmentId: userAssignment.assessmentId,
      assignmentID: userAssignment.id,
      userId: userAssignment.userId,
      answer: userAnswer,
      questionId: question.id,
      date: Date.now(),
      // for now its hard coded, but we need to save framework name in risk as well.
      frameworkName: frameName,
      riskAction: RiskAction.MANAGED,
      isActionTaken: true,
      left: question.left,
      right: question.right,
    };
    await this.customApi.CreateAnswer(createAnswer);
  }

  /**
   * Following are the functions to cater the Auto Linking of Manual Tasks in
   * Semi-Auto and Manual Risks.
   */

  /**
   * The function updates the manual tasks status's in the Risk Table
   * @param taskObjList The Task Obj with Task ID and Assessment ID
   * @param entityId The root Entity ID
   * @returns
   */
  async updateRiskManualTaskStatus(taskObjList: any, entityId: string): Promise<void> {
    const filterArr: any[] = [];

    // first we will build the Filter Query to get all the risks containing the Task Ids
    Object.keys(taskObjList).forEach((tk: any) => {
      filterArr.push({
        assessmentId: {
          eq: taskObjList[tk].assessmentId,
        },
        riskTasksIds: {
          contains: tk,
        },
      });
    });
    const filterInput: ModelRiskFilterInput = {
      or: filterArr,
    };
    let riskListFound = null;
    try {
      const cacheList = await this.customApi.RiskByEntityId(entityId, undefined, filterInput, 1000, undefined);
      riskListFound = cacheList.items;
      let completeCounter = 0;
      const updateRisks: any[] = [];
      if (riskListFound && riskListFound.length) {
        // if the Risk is found , update the Task Status
        riskListFound.forEach((rsk: any) => {
          completeCounter = 0;
          if (rsk.riskTasks && rsk.riskTasks.length) {
            rsk.riskTasks.forEach((task: any) => {
              if (taskObjList[task.id]) {
                task.status = TaskStatusEnum.REMEDIATED;
              }
              if (task.status === TaskStatusEnum.REMEDIATED) {
                completeCounter++;
              }
            });

            // if all Task status is Updated to Mitigated , then Change Risk Status as well or vice versa
            if (completeCounter === rsk.riskTasks.length && rsk.type === RiskTypeEnum.HARD_MANUAL) {
              rsk.riskStatus = RiskStatusEnum.MITIGATED;
            }
            if (completeCounter === rsk.riskTasks.length && rsk.type === RiskTypeEnum.MANUAL) {
              this.mitigateRiskBaseOnScoreHistory(rsk);
            }
            updateRisks.push(rsk);
          }
        });

        // Updating the Risk Instance in DB
        if (updateRisks && updateRisks.length) {
          const promises: any[] = [];
          updateRisks.forEach((risk: any) => {
            promises.push(this.updateRisk(risk, false, false));
          });
          await Promise.all(promises);
        }
      }
    } catch (e: any) {
      console.log(e);
      return;
    }
  }

  manualTasksStatusCheck(taskList: any): boolean {
    let counter = 0;
    taskList.forEach((tsk: any) => {
      if (tsk.status === TaskStatusEnum.REMEDIATED) {
        counter++;
      }
    });
    return counter === taskList.length;
  }

  /**
   * To Update the Task Status in CustomTask Table
   * @param taskList The task list containing Task ID and Task Status
   */
  async updateTaskInRemediation(taskList: any[]): Promise<void> {
    await Promise.all(
      taskList.map(async (tsk: any) => {
        await this.customApi.UpdateCustomTask({
          id: tsk.id,
          status: tsk.status as TaskStatusEnum,
          assessmentId: tsk?.assessmentId,
        });
      })
    );
  }

  async setSubEntitiesBaseOnRoot(): Promise<void> {
    if (!this.rootEntityId) {
      return;
    }
    try {
      this.subEntities = await this.rootEntityContextService.getSubEntities('CHILD_ENTITY');
      // this.nistSubEntities = this.subEntities;
    } catch (e: any) {
      console.log('Error - setSubEntitiesBaseOnRoot: ', e);
    }
  }

  async getSubEntitiesByOnRootId(): Promise<void> {
    if (!this.rootEntityId) {
      return;
    }
    try {
      this.subEntitiesByRootId = await this.rootEntityContextService.getSubEntities('CHILD_ENTITY');
      this.subEntities = this.subEntitiesByRootId;
    } catch (e: any) {
      console.log('Error - getSubEntitiesByOnRootId: ', e);
    }
  }

  formattedRiskTitlesBaseOnTypes(riskList: any): IRiskTypes {
    return riskList?.reduce((prev: any, current: any) => {
      if (prev[current?.type]) {
        prev[current.type].push(current?.idTitle);
      } else {
        prev[current?.type] = [];
        prev[current?.type].push(current?.idTitle);
      }
      return prev;
    }, {});
  }

  getRiskIdTitle(riskIdTitlesMap: any, type: string, stringPart: any = ''): string {
    // stringPart can have custom string like for bulk upload where we will pass
    // RB for risk bulk
    if (riskIdTitlesMap && type) {
      let idTitle: any;
      switch (type.toLowerCase()) {
        case RiskTypeEnum.AUTOMATIC.toLowerCase(): {
          stringPart ||= 'RA';
          break;
        }
        case RiskTypeEnum.MANUAL.toLowerCase(): {
          stringPart ||= 'RS';
          break;
        }
        case RiskTypeEnum.HARD_MANUAL.toLowerCase(): {
          stringPart ||= 'RM';
          break;
        }
      }
      let count = riskIdTitlesMap[type] ? riskIdTitlesMap[type].length : 0;
      idTitle = `${stringPart + count}`;
      if (riskIdTitlesMap[type]) {
        while (riskIdTitlesMap[type].includes(idTitle)) {
          count++;
          idTitle = `${stringPart + count}`;
        }
        riskIdTitlesMap[type].push(idTitle);
      } else {
        idTitle = `${stringPart + 0}`;
        riskIdTitlesMap[type] = [idTitle];
      }
      return idTitle;
    }

    return '';
  }

  getIsArchiveMode(): boolean {
    return this.isArchiveView;
  }

  // find any attribute which is empty or not equivalent to default values for some attribute
  // are corrupted risk, use in bulk upload
  isCorruptedRisk(risk: any): boolean {
    if (!risk) {
      return false;
    }
    return (
      Object.values(risk).some((r: any) => !r) ||
      !UtilsService.toLowerCaseAll(Object.keys(RiskTableConstant.ASSET_ITEMS)).includes(
        risk[RiskColumnMappersEnum.affectedAssets][0]?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(ProbabilityEnum)).includes(
        risk[RiskColumnMappersEnum.probability]?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(RiskConfidenceEnum)).includes(
        risk[RiskColumnMappersEnum.priority]?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(ImpactEnum)).includes(
        risk[RiskColumnMappersEnum.impact]?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(ImpactEnum)).includes(
        UtilsService.getLabelValueBaseOnNum(risk[RiskColumnMappersEnum.inherent])?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(ProbabilityEnum)).includes(
        UtilsService.getLabelValueBaseOnNum(risk[RiskColumnMappersEnum.residual], 2)?.toLowerCase()
      ) ||
      !UtilsService.toLowerCaseAll(Object.values(StatusEnum)).includes(
        risk[RiskColumnMappersEnum.status]?.toLowerCase()
      )
    );
  }

  async getRiskComments(riskId: string, assessmentId: string): Promise<CommentsByRiskIdQuery[]> {
    try {
      let results: any[] = [];
      let nextToken: string | null | undefined;
      const filter = {
        assessmentId: {
          eq: assessmentId,
        },
      };
      do {
        const result = await this.customApi.CommentsByRiskId(riskId, undefined, filter, 10000, nextToken || undefined);
        results = results.concat(result.items);
        nextToken = result.nextToken;
      } while (nextToken);
      return results;
    } catch (error: any) {
      console.log(error);
      return [];
    }
  }
  async getRiskCommentsByGroupId(groupId: string): Promise<CommentsByRiskIdQuery[]> {
    try {
      let results: any[] = [];
      let nextToken: string | null | undefined;
      do {
        const result = await this.customApi.CommentsByGroupId(
          groupId,
          undefined,
          undefined,
          10000,
          nextToken || undefined
        );
        results = results.concat(result.items);
        nextToken = result.nextToken;
      } while (nextToken);
      return results;
    } catch (error: any) {
      console.log(error);
      return [];
    }
  }

  async getRiskOwners(riskId: string): Promise<any[]> {
    try {
      let results: any = [];
      let nextToken: string | null | undefined;
      do {
        const result = await this.customApi.RiskAssignmentByRiskId(
          riskId,
          undefined,
          undefined,
          10000,
          nextToken || undefined
        );
        results = results.concat(result.items);
        nextToken = result.nextToken;
      } while (nextToken);
      return results;
    } catch (error: any) {
      console.log(error);
      return [];
    }
  }

  async createComment(comment: CreateCommentInput): Promise<GetCommentQuery> {
    return await this.customApi.CreateComment(comment);
  }

  async updateComment(comment: UpdateCommentInput): Promise<GetCommentQuery> {
    return await this.customApi.UpdateComment(comment);
  }

  async deleteComment(comment: DeleteCommentInput, assessmentId: string): Promise<GetCommentQuery> {
    return await this.customApi.DeleteComment(comment, { assessmentId: { eq: assessmentId } });
  }
  async deleteCommentByGroupId(comment: DeleteCommentInput, groupId: string): Promise<GetCommentQuery | undefined> {
    try {
      return await this.customApi.DeleteComment(comment, { groupId: { eq: groupId } });
    } catch (err: any) {
      console.log(err);
    }
  }

  createLogsAfterRiskUpdated(currentUser: any, oldRisk: any, newRisk: any): void {
    if (oldRisk.riskDescription !== newRisk.riskDescription) {
      this.addLog(
        newRisk?.id,
        newRisk?.assessmentId,
        getLogMessage(LogsKeyEnum.DESCRIPTION_CHANGED_RISK, currentUser?.name)
      );
    }
    if (oldRisk.riskStatus !== newRisk.riskStatus) {
      this.addLog(
        newRisk?.id,
        newRisk?.assessmentId,
        getLogMessage(LogsKeyEnum.STATUS_CHANGED_RISK, currentUser?.name, newRisk.riskStatus.toLowerCase())
      );
    }
    if (
      !this.compareTags(
        newRisk.riskTags?.length ? newRisk.riskTags : [],
        oldRisk.riskTags?.length ? oldRisk.riskTags : []
      )
    ) {
      this.addLog(newRisk?.id, newRisk?.assessmentId, getLogMessage(LogsKeyEnum.TAGS_ADDED_RISK, currentUser?.name));
    }
    if (
      !this.compareTags(
        newRisk.relatedAssets?.length ? newRisk.relatedAssets : [],
        oldRisk.relatedAssets?.length ? oldRisk.relatedAssets : []
      )
    ) {
      this.addLog(newRisk?.id, newRisk?.assessmentId, getLogMessage(LogsKeyEnum.ASSETS_ADDED_RISK, currentUser?.name));
    }
  }

  compareTags(a: [string], b: [string]): boolean {
    const first = a.map((item: any) => item.toUpperCase());
    const second = b.map((item: any) => item.toUpperCase());
    return first.length === second.length && first.every((element: any) => second.includes(element));
  }

  // to save a log related to Risk targetId pattern is 'assessmentId#riskId'
  // type may contain useful keywords like RISK#<idTitle>
  async addLog(riskId: string, assessmentId: string | null, message: string): Promise<CreateLogsInput> {
    let targetId: any;
    assessmentId = assessmentId && assessmentId.includes('#') ? assessmentId.split('#')[0] : assessmentId;
    if (assessmentId) {
      targetId = `${assessmentId}#${riskId}`;
    } else {
      assessmentId = null;
      targetId = `${riskId}`;
    }
    let type: any;
    if (assessmentId) {
      type = LogsTypeEnum.RISK + '#' + riskId.split('#')[1];
    } else {
      type = LogsTypeEnum.RISK_GROUP + '#' + riskId.split('#')[1];
    }

    const log = {
      id: uuid(),
      message,
      targetId,
      type,
      assessmentId,
    };
    return await this.customApi.CreateLogs(log);
  }

  async addLineChartData(
    riskID: string,
    assessmentID: string,
    inherentValue: RiskImpactEnum,
    residualValue: RiskImpactEnum
  ): Promise<CreateRiskLineChartMutation> {
    try {
      const chartData = {
        assessmentId: assessmentID,
        riskId: riskID,
        residual: residualValue,
        inherent: inherentValue,
      };
      return await this.customApi.CreateRiskLineChart(chartData);
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  async getLineChartData(riskId: string, startDate?: Date, endDate?: Date): Promise<any> {
    try {
      this.lastLineChartRiskId = riskId;
      const limit = 300;
      let nextToken: any;
      let lineChartData: any[] = [];
      this.riskGraphLineData = [];

      let filter: ModelRiskLineChartFilterInput | undefined;
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setUTCHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setUTCHours(23, 59, 59, 999);
        filter = {
          createdAt: {
            between: [start.toISOString(), end.toISOString()],
          },
        };
      }

      do {
        const { items: linechart, nextToken: rnextToken } = await this.customApi.RiskLineChartbyRiskId(
          riskId,
          undefined,
          filter,
          limit,
          nextToken
        );
        nextToken = rnextToken;
        lineChartData = lineChartData.concat(linechart);
      } while (nextToken);
      this.riskGraphLineData = lineChartData?.length ? [...lineChartData] : [];
      return lineChartData;
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  async getLogsByTargetId(targetId: string): Promise<LogsByTargetIdQuery> {
    try {
      const logs: LogsByTargetIdQuery = await this.customApi.LogsByTargetId(targetId);
      return logs;
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  getQuestionAnswer = (question: any, frameworkKey: any): { answer: number | null; answerName: string | null } => {
    const score = { answer: null, answerName: null };

    if (question?.settings?.isCollaborative === false) {
      const answersArray = question?.managerAnswers?.length
        ? question.managerAnswers
        : question?.answers?.length
          ? question.answers
          : [];
      question.answers = answersArray;
      const avgScore = UtilsService.calculateAverageObject(question, frameworkKey);
      score.answer = avgScore.answer;
      score.answerName = avgScore.answerName;
    } else {
      const answer = question?.managerAnswers?.length
        ? question.managerAnswers[0]
        : question?.answers?.length
          ? question.answers[0]
          : null;
      score.answer = answer?.answer;
      score.answerName = answer?.answerName;
    }
    return score;
  };

  async getRiskGroup(entityId: string): Promise<any> {
    let groupData: any[] = [];
    try {
      // Source risk groups from the shared RootEntityContextService cache (kept live by AppSync
      // subscriptions) rather than an independent RiskGroupByEntityId query.
      groupData = await this.rootEntityContextService.getRiskGroupsByEntityId(entityId);
    } catch {
      console.log('Error while fetching risk groups');
    }
    this.riskTableDataHandling(groupData, true);
    return groupData;
  }
  riskTableDataHandling = (data: any, type: any, assessmentId: any = null) => {
    if (type) {
      this.riskTableData.groupData = [...data];
    } else {
      if (assessmentId) {
        this.riskTableData.allRisk[assessmentId] = [...data];
      }
    }
  };
  filterSubEntityByAssessmentId(assessmentId: string): any {
    const filtered = this.subEntities.filter((sub: any) => sub.activeAssessmentId === assessmentId);
    return filtered.length ? filtered[0] : null;
  }

  async getTagsByTargetId(targetId: string): Promise<any> {
    try {
      const tags = await this.rootEntityContextService.getTagsByTargetId(targetId);
      return tags || [];
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  async getCategoriesByTargetId(targetId: string): Promise<any> {
    try {
      const categories = await this.rootEntityContextService.getCategoriesByTargetId(targetId);
      return categories || [];
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  async fetchAllTagsAgainstRootEntity(): Promise<void> {
    try {
      const tags = await this.rootEntityContextService.getTagsByEntityId(this.rootEntityId);
      if (tags?.length) {
        tags.forEach((tag: any) => {
          if (tag.targetId === '_Global') {
            return;
          }
          if (!this.allTagsAgainstRootEntity[tag.targetId]) {
            this.allTagsAgainstRootEntity[tag.targetId] = [tag];
          } else if (!this.allTagsAgainstRootEntity[tag.targetId].some((t: any) => t.id === tag.id)) {
            this.allTagsAgainstRootEntity[tag.targetId].push(tag);
          }
        });
      }
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  async fetchAllCategoriesAgainstRootEntity(): Promise<void> {
    try {
      const categories = await this.rootEntityContextService.getCategoriesByEntityId(this.rootEntityId);
      if (categories?.length) {
        categories.forEach((category: any) => {
          if (category.targetId === '_Global') {
            return;
          }
          if (!this.allCategoriesAgainstRootEntity[category.targetId]) {
            this.allCategoriesAgainstRootEntity[category.targetId] = [category];
          } else if (!this.allCategoriesAgainstRootEntity[category.targetId].some((c: any) => c.id === category.id)) {
            this.allCategoriesAgainstRootEntity[category.targetId].push(category);
          }
        });
      }
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  //* Map Score to Partial Answer
  public mapScoreToPartialAnswer(
    score: any,
    partialRepresentation: PartialRepresentationEnum = PartialRepresentationEnum.LEVEL
  ): string {
    let result = '';
    if (partialRepresentation === PartialRepresentationEnum.PERCENTAGE) {
      const sliderPercentage = UtilsService.partialPercentageMapping(score, false);
      result = sliderPercentage + '%';
    } else {
      const sliderPosition = UtilsService.partialAnswersMapping(score, false);
      // Convert the slider position back to percentage
      const sliderPercentage = UtilsService.partialAnswersMapping(sliderPosition, true);
      // Display the correct mapped percentage
      result = sliderPercentage * 10 + '%';
    }
    return result;
  }
}
